/**
 * Minouko — comportements de la boutique.
 * JavaScript natif, aucun framework, aucune dépendance.
 * Tout ce qui est ici dégrade proprement : sans JS, les formulaires postent
 * normalement et les liens fonctionnent.
 */
(() => {
  'use strict';

  const euro = (cents) =>
    (cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) +
    ' €';

  /** Le CDN Shopify redimensionne via ?width=, pas via le suffixe _200x hérité. */
  const sized = (url, w) => {
    try {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('width', String(w));
      return u.toString();
    } catch (_) {
      return url;
    }
  };

  /**
   * Les API Shopify n'expriment pas les prix de la même façon : l'API panier et
   * celle des recommandations renvoient des centiemes entiers, celle de
   * suggestion une chaîne décimale. Sans normalisation, un prix sort cent fois
   * trop élevé.
   */
  const toCents = (v) => {
    if (typeof v === 'number') return Math.round(v);
    const str = String(v).trim();
    if (/[.,]/.test(str)) return Math.round(parseFloat(str.replace(',', '.')) * 100);
    return Math.round(Number(str));
  };

  const focusable =
    'a[href], summary, button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  /* ───────────────────────── Tiroir panier ───────────────────────── */

  const Drawer = {
    el: null,
    panel: null,
    body: null,
    foot: null,
    lastFocus: null,

    init() {
      this.el = document.getElementById('cart-drawer');
      if (!this.el) return;
      this.panel = this.el.querySelector('.drawer__panel');
      this.body = this.el.querySelector('[data-drawer-body]');
      this.foot = this.el.querySelector('[data-drawer-foot]');

      this.el.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]') || e.target === this.el.querySelector('.drawer__veil')) {
          this.close();
        }
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
        if (e.key === 'Tab' && this.isOpen()) this.trap(e);
      });

      document.addEventListener('click', (e) => {
        if (!e.target.closest('[data-cart-open]')) return;
        e.preventDefault();
        this.open();
      });

      this.body.addEventListener('click', (e) => {
        const step = e.target.closest('[data-step]');
        if (step) {
          e.preventDefault();
          const line = Number(step.dataset.line);
          const qty = Number(step.dataset.qty);
          this.change(line, qty);
        }
      });
    },

    isOpen() {
      return this.el && this.el.hasAttribute('open');
    },

    trap(e) {
      const nodes = [...this.panel.querySelectorAll(focusable)].filter((n) => n.offsetParent !== null);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },

    async open(source) {
      if (!this.el) return;
      this.lastFocus = source || document.activeElement;
      this.el.inert = false;
      this.el.setAttribute('open', '');
      document.documentElement.style.overflow = 'hidden';
      const target = this.panel.querySelector('button[data-drawer-close]');
      if (target) target.focus();
      await this.refresh();
    },

    close() {
      if (!this.el) return;
      this.el.removeAttribute('open');
      this.el.inert = true;
      document.documentElement.style.overflow = '';
      if (this.lastFocus) this.lastFocus.focus();
    },

    async refresh() {
      try {
        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        this.render(cart);
        Drawer.setCount(cart.item_count);
      } catch (_) {
        this.body.innerHTML = '<p class="drawer__msg">Le panier est momentanément indisponible. Rechargez la page.</p>';
      }
    },

    setCount(n) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = n > 0 ? n : '';
        el.hidden = n === 0;
      });
    },

    render(cart) {
      if (!cart.items.length) {
        this.body.innerHTML =
          '<div class="drawer__empty"><p>Votre panier est vide.</p>' +
          '<p><a href="/collections">Parcourir les rayons</a></p></div>';
        this.foot.hidden = true;
        return;
      }
      this.foot.hidden = false;
      this.body.innerHTML =
        '<ul class="dlines">' +
        cart.items
          .map((it, i) => {
            const line = i + 1;
            const img = it.image ? `<img src="${sized(it.image, 200)}" alt="" decoding="async">` : '';
            const variant =
              it.options_with_values && it.options_with_values.length > 1
                ? `<p class="dline__v">${it.options_with_values
                    .filter((o) => !/ships?\s*from|origin/i.test(o.name))
                    .map((o) => o.value)
                    .join(' · ')}</p>`
                : '';
            return `<li class="dline">
              <a class="dline__ph" href="${it.url}" tabindex="-1" aria-hidden="true">${img}</a>
              <div class="dline__body">
                <h3 class="dline__t"><a href="${it.url}">${it.product_title}</a></h3>
                ${variant}
                <dl class="line-properties">${Object.entries(it.properties || {})
                  .filter(([name, value]) => !name.startsWith('_') && value != null && value !== '')
                  .map(([name, value]) => `<div><dt>${escapeProperty(name)}</dt><dd>${escapeProperty(value)}</dd></div>`)
                  .join('')}</dl>
                <div class="stepper" role="group" aria-label="Quantité">
                  <button type="button" data-step data-line="${line}" data-qty="${it.quantity - 1}" aria-label="Retirer un exemplaire">&minus;</button>
                  <span class="num" aria-live="polite">${it.quantity}</span>
                  <button type="button" data-step data-line="${line}" data-qty="${it.quantity + 1}" aria-label="Ajouter un exemplaire">+</button>
                </div>
              </div>
              <div class="dline__end">
                <span class="num">${euro(it.final_line_price)}</span>
                <button type="button" class="dline__rm" data-step data-line="${line}" data-qty="0">Retirer</button>
              </div>
            </li>`;
          })
          .join('') +
        '</ul>';
      this.foot.querySelector('[data-drawer-total]').textContent = euro(cart.total_price);
      this.crossSell(cart);
    },

    /**
     * Vente croisée. On part du dernier article ajouté et on propose ses
     * voisins de rayon, en excluant ce qui est déjà au panier. Rien ne
     * s'affiche si Shopify ne renvoie rien : pas de bloc vide.
     */
    async crossSell(cart) {
      const host = this.el.querySelector('[data-drawer-cross]');
      if (!host || !cart.items.length) return;
      const seed = cart.items[cart.items.length - 1];
      if (host.dataset.seed === String(seed.product_id)) return;
      host.dataset.seed = String(seed.product_id);
      const inCart = new Set(cart.items.map((i) => i.product_id));
      try {
        const d = await fetch(
          `/recommendations/products.json?product_id=${seed.product_id}&limit=6&intent=related`,
          { headers: { Accept: 'application/json' } }
        ).then((r) => r.json());
        const picks = (d.products || []).filter((p) => !inCart.has(p.id)).slice(0, 3);
        if (!picks.length) {
          host.innerHTML = '';
          return;
        }
        host.innerHTML =
          '<h3 class="cross__h">À compléter</h3><ul class="cross">' +
          picks
            .map((p) => {
              const src =
                (p.featured_image && (p.featured_image.url || p.featured_image)) ||
                (p.images && p.images[0]) ||
                null;
              const img = src ? `<img src="${sized(src, 120)}" alt="" decoding="async">` : '';
              return `<li><a href="${p.url}">
                <span class="cross__ph">${img}</span>
                <span class="cross__t">${p.title}</span>
                <span class="price num">${euro(toCents(p.price))}</span>
              </a></li>`;
            })
            .join('') +
          '</ul>';
      } catch (_) {
        host.innerHTML = '';
      }
    },

    async change(line, quantity) {
      this.body.setAttribute('aria-busy', 'true');
      try {
        const cart = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity }),
        }).then((r) => r.json());
        this.render(cart);
        Drawer.setCount(cart.item_count);
      } finally {
        this.body.removeAttribute('aria-busy');
      }
    },
  };

  /* ─────────────── Ajout au panier, depuis n'importe où ─────────────── */

  function escapeProperty(value) {
    const text = document.createElement('span');
    text.textContent = String(value);
    return text.innerHTML;
  }

  function submitProduct(form, button) {
    if (form.dataset.submitting) return;
    const idField = form.querySelector('[name="id"]');
    if (!idField || idField.disabled || !idField.value) return;
    form.querySelectorAll('[data-engraving-field]').forEach((field) => {
      field.value = field.value.trim();
    });
    if (!form.reportValidity()) return;
    const properties = {};
    new FormData(form).forEach((value, name) => {
      const match = name.match(/^properties\[(.+)\]$/);
      if (match && typeof value === 'string') properties[match[1]] = value;
    });
    const qty = form.querySelector('[name="quantity"]');
    form.dataset.submitting = 'true';
    addToCart(idField.value, qty ? Number(qty.value) : 1, button, properties)
      .finally(() => { delete form.dataset.submitting; });
  }

  async function addToCart(id, quantity, button, properties = {}) {
    if (button?.disabled) return;
    const cardLabel = button?.querySelector('[data-card-add-label]');
    const cardLabelText = cardLabel?.textContent;
    if (cardLabel) cardLabel.textContent = 'Ajout…';
    let feedback = null;
    if (button) {
      const host = button.closest('[data-product-form], [data-card-product-form], .cartel, .stickybuy__in') || button.parentElement;
      feedback = host.querySelector('[data-cart-feedback]');
      if (!feedback) {
        feedback = document.createElement('p');
        feedback.setAttribute('data-cart-feedback', '');
        feedback.setAttribute('role', 'status');
        host.append(feedback);
      }
      feedback.textContent = '';
    }
    if (button) {
      button.disabled = true;
      button.dataset.busy = 'true';
      button.setAttribute('aria-busy', 'true');
    }
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: quantity || 1, properties }] }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Ajout impossible.');
      }
      await Drawer.open(button);
    } catch (e) {
      if (feedback) feedback.textContent = e.message || 'Ajout impossible. Réessayez dans un instant.';
    } finally {
      if (button) {
        button.disabled = false;
        delete button.dataset.busy;
        button.removeAttribute('aria-busy');
        if (cardLabel) cardLabel.textContent = cardLabelText;
      }
    }
  }

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-product-form], [data-card-product-form]');
    if (!form) return;
    e.preventDefault();
    submitProduct(form, form.querySelector('[type="submit"]'));
  });

  /* ───────────────────────── Galerie produit ───────────────────────── */

  function gallery() {
    const root = document.querySelector('[data-gallery]');
    if (!root || root.dataset.galleryReady) return;
    root.dataset.galleryReady = 'true';
    const main = root.querySelector('[data-gallery-main]');
    const thumbs = [...root.querySelectorAll('[data-gallery-thumb]')];
    if (!main || !thumbs.length) return;

    const show = (btn) => {
      const full = btn.dataset.full;
      if (!full) return;
      main.src = full;
      main.srcset = '';
      main.alt = btn.dataset.alt || '';
      thumbs.forEach((t) => t.setAttribute('aria-current', t === btn ? 'true' : 'false'));
    };

    thumbs.forEach((btn) => {
      btn.addEventListener('click', () => show(btn));
      btn.addEventListener('keydown', (e) => {
        const i = thumbs.indexOf(btn);
        if (e.key === 'ArrowRight' && thumbs[i + 1]) {
          e.preventDefault();
          thumbs[i + 1].focus();
          show(thumbs[i + 1]);
        }
        if (e.key === 'ArrowLeft' && thumbs[i - 1]) {
          e.preventDefault();
          thumbs[i - 1].focus();
          show(thumbs[i - 1]);
        }
      });
    });
  }

  /* ─────────────────────── Sélection de variante ─────────────────────── */

  function variants() {
    const form = document.querySelector('[data-product-form]');
    if (!form || form.dataset.variantsReady) return;
    const data = document.getElementById('variant-data');
    if (!data) return;

    let list;
    try {
      list = JSON.parse(data.textContent);
    } catch (_) {
      return;
    }

    const idField = form.querySelector('[name="id"]');
    const priceWrap = document.querySelector('[data-variant-price-wrap]');
    const priceEl = priceWrap ? priceWrap.querySelector('.num') : null;
    const compareEl = priceWrap ? priceWrap.querySelector('.price__was') : null;
    const stickyPrice = document.querySelector('[data-sticky-price]');
    const submit = form.querySelector('[type="submit"]');
    const submitPrice = submit ? submit.querySelector('[data-submit-price]') : null;

    form.dataset.variantsReady = 'true';
    const apply = () => {
      const chosen = [...form.querySelectorAll('input[type="radio"]:checked')].reduce((acc, input) => {
        acc[Number(input.dataset.optionIndex)] = input.value;
        return acc;
      }, {});
      const matches = list.filter((v) =>
        Object.entries(chosen).every(([i, val]) => v.options[Number(i)] === val)
      );
      const match = matches.find(v => String(v.id) === idField.value && v.available)
        || matches.find(v => v.available) || matches[0];
      const status = document.querySelector('[data-variant-status]');
      const stickyBtn = document.querySelector('[data-sticky-add]');
      const valid = Boolean(match && match.available);
      idField.disabled = !match;
      if (submit) {
        submit.disabled = !valid;
        const label = submit.querySelector('[data-submit-label]');
        if (label) label.textContent = valid ? 'Ajouter au panier' : match ? 'Épuisé' : 'Combinaison indisponible';
      }
      if (stickyBtn) {
        stickyBtn.disabled = !valid;
        stickyBtn.textContent = valid ? 'Ajouter' : 'Indisponible';
      }
      if (status) status.textContent = valid ? 'Disponible à la commande' : match ? 'Cette option est épuisée' : 'Cette combinaison n’existe pas. Choisissez une autre option.';
      if (!match) {
        if (priceEl) priceEl.textContent = '—';
        if (compareEl) compareEl.hidden = true;
        if (submitPrice) submitPrice.textContent = '';
        if (stickyPrice) stickyPrice.textContent = '—';
        const url = new URL(window.location); url.searchParams.delete('variant'); history.replaceState({}, '', url);
        return;
      }
      idField.value = match.id;
      if (priceEl) priceEl.textContent = euro(match.price);
      if (compareEl) {
        const has = match.compare && match.compare > match.price;
        compareEl.hidden = !has;
        if (has) compareEl.textContent = euro(match.compare);
      }
      if (submitPrice) submitPrice.textContent = euro(match.price);
      if (stickyPrice) stickyPrice.textContent = euro(match.price);
      if (match.image) {
        const thumb = document.querySelector(`[data-image-id="${match.imageId}"]`);
        if (thumb) thumb.click();
        else {
          const main = document.querySelector('[data-gallery-main]');
          if (main) { main.src = match.image; main.srcset = ''; }
          document.querySelectorAll('[data-gallery-thumb]').forEach(t => t.setAttribute('aria-current', 'false'));
        }
      }
      const url = new URL(window.location);
      url.searchParams.set('variant', match.id);
      history.replaceState({}, '', url);
    };

    form.addEventListener('change', (e) => {
      if (e.target.matches('input[type="radio"]')) apply();
    });
  }

  document.addEventListener('shopify:section:load', () => { gallery(); variants(); });

  /* ───────────────────── Description dépliable ───────────────────── */

  function expanders() {
    document.querySelectorAll('[data-expand]').forEach((btn) => {
      const target = document.getElementById(btn.getAttribute('aria-controls'));
      if (!target) return;
      // Rien à déplier si le texte tient déjà.
      if (target.scrollHeight <= target.clientHeight + 4) {
        btn.hidden = true;
        target.classList.remove('is-clamped');
        return;
      }
      btn.addEventListener('click', () => {
        const open = target.classList.toggle('is-open');
        target.classList.toggle('is-clamped', !open);
        btn.setAttribute('aria-expanded', String(open));
        btn.textContent = open ? 'Réduire' : btn.dataset.expand;
      });
    });
  }




  /* ───────────── Barre d'achat collante (fiche produit) ───────────── */

  function stickyBuy() {
    const bar = document.querySelector('[data-stickybuy]');
    const form = document.querySelector('[data-product-form]');
    if (!bar || !form) return;
    const anchor = form.querySelector('[type="submit"]');
    if (!anchor) return;

    /* Elle apparaît quand le bouton principal a quitté le champ par le haut.
       Un IntersectionObserver ne prévient qu'au franchissement d'un seuil : un
       saut qui enjambe le bouton d'un seul coup, lien d'ancre, retour arrière ou
       geste rapide sur mobile, ne déclenche aucun appel, et la barre restait
       cachée pour le reste de la visite. On lit donc la position réelle, une fois
       par image au plus, à l'écoute passive du défilement. L'état est aussi
       calculé au chargement, pour une arrivée directe au milieu de la page. */
    let pending = false;
    let visible = null;

    /* La barre est fixée au bas du champ : sans réserve, elle recouvre la fin de
       la page, dont la mention de prix et de TVA du pied. On réserve sa hauteur
       réelle, et seulement quand elle est là. */
    const reserve = () => {
      document.body.style.paddingBottom = visible ? bar.offsetHeight + 'px' : '';
    };
    const settle = () => {
      pending = false;
      const passed = anchor.getBoundingClientRect().bottom <= 0;
      if (passed === visible) return;
      visible = passed;
      bar.hidden = !passed;
      reserve();
    };
    const schedule = () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(settle);
    };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', () => {
      schedule();
      reserve();
    }, { passive: true });
    settle();

    bar.querySelector('[data-sticky-add]').addEventListener('click', (e) => {
      submitProduct(form, e.currentTarget);
    });
  }

  /* ──────────────── Recommandations, chargées après coup ──────────────── */

  function recommendations() {
    const host = document.querySelector('[data-recos]');
    if (!host || !host.dataset.url) return;
    const load = () => {
      fetch(host.dataset.url)
        .then((r) => r.text())
        .then((html) => {
          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recos]');
          if (fresh && fresh.innerHTML.trim()) host.innerHTML = fresh.innerHTML;
        })
        .catch(() => {});
    };
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            io.disconnect();
            load();
          }
        },
        { rootMargin: '600px' }
      );
      io.observe(host);
    } else {
      load();
    }
  }



  /* ─────────────── Pas-à-pas de quantité ───────────────
     Le champ nombre natif reste la source de vérité : les boutons ne font que
     l'incrémenter et déclencher un `change`, pour que tout ce qui écoute le
     formulaire continue de fonctionner sans JavaScript dédié. */

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty]');
    if (!btn) return;
    const field = btn.closest('[data-qty-stepper]');
    const input = field && field.querySelector('input');
    if (!input) return;
    e.preventDefault();
    const min = Number(input.min || 0);
    const next = Math.max(min, (Number(input.value) || 0) + Number(btn.dataset.qty));
    if (next === Number(input.value)) return;
    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
    field.querySelectorAll('[data-qty="-1"]').forEach((b) => { b.disabled = next <= min; });
  });

  function syncSteppers() {
    document.querySelectorAll('[data-qty-stepper]').forEach((f) => {
      const input = f.querySelector('input');
      const minus = f.querySelector('[data-qty="-1"]');
      if (input && minus) minus.disabled = Number(input.value) <= Number(input.min || 0);
    });
  }

  /* ─────────────────── Produits récemment vus ───────────────────
     Écrit et lu dans le navigateur du visiteur. Rien ne part au serveur,
     rien n'est conservé ailleurs que sur son appareil. Toute erreur de
     stockage (navigation privée, quota) est avalée : la section disparaît
     simplement, elle ne casse jamais la page. */

  const RV_KEY = 'minouko:vus';
  const RV_MAX = 12;

  function rvRead() {
    try {
      const raw = localStorage.getItem(RV_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (_) {
      return [];
    }
  }

  function rvRemember() {
    const meta = document.getElementById('rv-current');
    if (!meta) return;
    let item;
    try {
      item = JSON.parse(meta.textContent);
    } catch (_) {
      return;
    }
    if (!item || !item.url) return;
    try {
      const list = rvRead().filter((p) => p.url !== item.url);
      list.unshift(item);
      localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, RV_MAX)));
    } catch (_) {
      /* Stockage indisponible : on n'insiste pas. */
    }
  }

  function rvRender() {
    const host = document.querySelector('[data-recently-viewed]');
    if (!host) return;
    const grid = host.querySelector('[data-rv-grid]');
    const current = window.location.pathname;
    const items = rvRead()
      .filter((p) => p.url !== current)
      .slice(0, Number(host.dataset.count || 4));

    if (!items.length) {
      host.hidden = true;
      return;
    }
    grid.innerHTML = items
      .map(
        (p) => `<a class="rv__item" href="${p.url}">
          <span class="rv__ph">${p.img ? `<img src="${sized(p.img, 420)}" alt="" decoding="async">` : ''}</span>
          <span class="rv__t">${p.title}</span>
          <span class="price num">${euro(p.cents)}</span>
        </a>`
      )
      .join('');
    host.hidden = false;

    const clear = host.querySelector('[data-rv-clear]');
    if (clear) {
      clear.addEventListener('click', () => {
        try {
          localStorage.removeItem(RV_KEY);
        } catch (_) {}
        host.hidden = true;
      });
    }
  }

  /* ─────────────────── Tiroir de navigation (mobile) ─────────────────── */

  function navDrawer() {
    let lastFocus = null;
    let savedOverflow = '';
    const inerted = new Map();
    const desktop = window.matchMedia('(min-width: 1260px)');
    const drawer = () => document.getElementById('nav-drawer');
    const summaryOf = group => group.querySelector(':scope > summary');
    const mobileGroup = () => drawer()?.querySelector('[data-menu-family][open]');
    const closeDesktop = (restoreFocus = false, except = null) => {
      document.querySelectorAll('.header__menu [data-menu-family][open]').forEach(group => {
        if (group === except) return;
        const ownsFocus = group.contains(document.activeElement);
        group.open = false;
        if (restoreFocus && ownsFocus) summaryOf(group)?.focus();
      });
    };
    const measure = () => {
      const header = document.querySelector('[data-mega-header]');
      if (!header) return;
      const bottom = Math.max(0, header.getBoundingClientRect().bottom);
      header.parentElement.style.setProperty('--mega-header-bottom', `${bottom}px`);
    };
    const resetFamilies = () => {
      drawer()?.querySelectorAll('[data-menu-family][open]').forEach(group => { group.open = false; });
      const nav = drawer()?.querySelector('.mega-mobile');
      if (nav) nav.scrollTop = 0;
    };
    const returnToFamilies = () => {
      const group = mobileGroup();
      if (!group) return false;
      group.open = false;
      summaryOf(group)?.focus();
      return true;
    };
    const isolateDrawer = el => {
      // Isolate siblings at every ancestor level, including the header itself.
      // Restore previous inert states instead of clearing another component's state.
      for (let parent = el; parent && parent !== document.body; parent = parent.parentElement) {
        for (const sibling of parent.parentElement?.children || []) {
          if (sibling === parent || inerted.has(sibling)) continue;
          inerted.set(sibling, sibling.inert);
          sibling.inert = true;
        }
      }
    };
    const close = (restoreFocus = true) => {
      const el = drawer();
      if (!el?.hasAttribute('open') && !inerted.size) return;
      el?.removeAttribute('open');
      if (el) el.inert = true;
      document.documentElement.style.overflow = savedOverflow;
      inerted.forEach((previous, node) => { node.inert = previous; });
      inerted.clear();
      document.querySelectorAll('[data-nav-open]').forEach(button => button.setAttribute('aria-expanded', 'false'));
      // Keep the selected family visible during the closing transition.
      // Opening the drawer resets it before it becomes interactive again.
      if (restoreFocus && lastFocus?.isConnected) lastFocus.focus();
    };
    const open = trigger => {
      const el = drawer();
      if (!el || desktop.matches || el.hasAttribute('open')) return;
      closeDesktop();
      resetFamilies();
      lastFocus = trigger;
      savedOverflow = document.documentElement.style.overflow;
      isolateDrawer(el);
      el.inert = false;
      el.setAttribute('open', '');
      document.documentElement.style.overflow = 'hidden';
      trigger.setAttribute('aria-expanded', 'true');
      el.querySelector('button[data-nav-close]')?.focus();
    };
    document.addEventListener('click', event => {
      const trigger = event.target.closest('[data-nav-open]');
      if (trigger) { event.preventDefault(); open(trigger); return; }
      if (event.target.closest('[data-nav-close]')) { close(); return; }
      const summary = event.target.closest('[data-menu-family] > summary');
      const group = summary?.parentElement;
      if (group?.closest('#nav-drawer')) {
        event.preventDefault();
        if (group.open) { returnToFamilies(); return; }
        resetFamilies();
        group.open = true;
        group.querySelector('h2')?.focus({ preventScroll: true });
        return;
      }
      if (group?.closest('.header__menu')) {
        closeDesktop(false, group);
        measure();
        // Let native details handle Enter, Space and click toggling.
        return;
      }
      if (event.target.closest('[data-search-open]')) close(false);
      document.querySelectorAll('.header__menu [data-menu-family][open]').forEach(openGroup => {
        if (!openGroup.contains(event.target)) openGroup.open = false;
      });
    });
    document.addEventListener('keydown', event => {
      const el = drawer();
      if (event.key === 'Escape') {
        if (el?.hasAttribute('open')) {
          event.preventDefault();
          if (!returnToFamilies()) close();
        } else closeDesktop(true);
      }
      if (event.key === '/' && el?.hasAttribute('open') && !/^(INPUT|TEXTAREA|SELECT)$/.test(event.target.tagName)) close(false);
      if (event.key === 'Tab' && el?.hasAttribute('open')) {
        const nodes = [...el.querySelectorAll(focusable)].filter(node => node.getClientRects().length && !node.closest('[inert]'));
        const first = nodes[0], last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    });
    document.addEventListener('focusin', event => {
      const el = drawer();
      if (el?.hasAttribute('open') && !el.contains(event.target)) {
        el.querySelector('button[data-nav-close]')?.focus();
        return;
      }
      document.querySelectorAll('.header__menu [data-menu-family][open]').forEach(group => {
        if (!group.contains(event.target)) group.open = false;
      });
    });
    document.addEventListener('toggle', event => {
      if (event.target.matches('.header__menu [data-menu-family][open]')) measure();
    }, true);
    desktop.addEventListener('change', () => {
      close(false);
      closeDesktop();
      measure();
      if (document.activeElement?.closest('#nav-drawer, .header__menu')) {
        const target = desktop.matches
          ? document.querySelector('.header__menu summary, .header__menu > a')
          : document.querySelector('[data-nav-open]');
        target?.focus();
      }
    });
    window.addEventListener('resize', measure, { passive: true });
    window.addEventListener('scroll', () => {
      if (document.querySelector('.header__menu [data-menu-family][open]')) measure();
    }, { passive: true });
    document.addEventListener('shopify:section:unload', event => {
      if (event.target.querySelector('#nav-drawer')) close(false);
    });
    document.addEventListener('shopify:section:load', () => {
      // A replacement header can arrive without an unload event.
      if (inerted.size && !drawer()?.hasAttribute('open')) close(false);
      measure();
    });
    document.addEventListener('shopify:block:select', event => {
      const group = event.target.closest('[data-menu-family]');
      if (!group) return;
      if (desktop.matches) {
        closeDesktop(false, group);
        group.open = true;
        measure();
      } else {
        open(document.querySelector('[data-nav-open]'));
        resetFamilies();
        const matching = [...drawer().querySelectorAll('[data-menu-family]')].find(item => item.dataset.menuTitle === group.dataset.menuTitle);
        if (matching) matching.open = true;
      }
    });
    measure();
  }

  /* ───────────────── Filtrage et tri de collection ───────────────── */

  function catalog() {
    if (document.documentElement.dataset.catalogReady) return;
    const firstRoot = document.querySelector('[data-catalog]');
    if (!firstRoot) return;
    document.documentElement.dataset.catalogReady = 'true';

    const desktop = window.matchMedia('(min-width: 900px)');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let controller = null;

    const setupRoot = (root) => {
      if (!root) return;
      root.dataset.catalogEnhanced = 'true';
      const filters = root.querySelector('.catalog-filters');
      if (filters) filters.open = desktop.matches;
    };

    const setupCurrentRoot = () => setupRoot(document.querySelector('[data-catalog]'));

    const requestedUrl = (form) => {
      const url = new URL(form.action, window.location.href);
      const params = new URLSearchParams();
      new FormData(form).forEach((value, name) => {
        if (typeof value === 'string' && value.trim() !== '') params.append(name, value);
      });
      url.search = params.toString();
      url.hash = 'collection-products';
      return url;
    };

    const focusSnapshot = (element) => {
      if (!element || !element.closest('[data-catalog]')) return null;
      return {
        id: element.id || '',
        name: element.name || '',
        value: element.value || '',
        type: element.type || '',
      };
    };

    const restoreFocus = (root, snapshot, fallback) => {
      let target = null;
      if (snapshot?.id) target = root.querySelector(`#${CSS.escape(snapshot.id)}`);
      if (!target && snapshot?.name) {
        target = [...root.querySelectorAll(`[name="${CSS.escape(snapshot.name)}"]`)].find(
          (field) => !snapshot.value || field.value === snapshot.value
        );
      }
      if (target?.matches('select') && target.closest('[data-enhanced]')) {
        target = target.closest('[data-ui-select]')?.querySelector('.ui-select__trigger') || target;
      }
      if (!target && fallback) target = root.querySelector(fallback);
      target?.focus({ preventScroll: true });
    };

    const announce = (root) => {
      const status = root.querySelector('[data-catalog-status]');
      const count = root.querySelector('[data-catalog-count]')?.textContent.trim();
      const range = root.querySelector('.collection-pagination__count')?.textContent.trim();
      if (status && count) status.textContent = `Sélection mise à jour : ${range || count}.`;
    };

    const update = async (url, { push = true, focus = null, fallback = '', scrollToProducts = false, animate = true } = {}) => {
      const root = document.querySelector('[data-catalog]');
      if (!root) return;
      const filtersWereOpen = root.querySelector('.catalog-filters')?.open || false;
      controller?.abort();
      controller = new AbortController();
      const currentController = controller;
      const status = root.querySelector('[data-catalog-status]');
      root.toggleAttribute('data-catalog-static', !animate);
      root.dataset.catalogLoading = 'true';
      root.setAttribute('aria-busy', 'true');
      if (status) status.textContent = 'Mise à jour de la sélection…';

      const fetchUrl = new URL(url, window.location.href);
      fetchUrl.hash = '';
      fetchUrl.searchParams.set('section_id', root.dataset.sectionId);

      try {
        const response = await fetch(fetchUrl, {
          headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
          signal: currentController.signal,
        });
        if (!response.ok) throw new Error(`Réponse ${response.status}`);
        const html = await response.text();
        const nextDocument = new DOMParser().parseFromString(html, 'text/html');
        const nextRoot = nextDocument.querySelector('[data-catalog]');
        if (!nextRoot) throw new Error('Section de collection absente');

        const swap = () => {
          root.dispatchEvent(new CustomEvent('shopify:section:unload', { bubbles: true }));
          root.replaceWith(nextRoot);
          setupRoot(nextRoot);
          if (!desktop.matches && filtersWereOpen) {
            const nextFilters = nextRoot.querySelector('.catalog-filters');
            if (nextFilters) nextFilters.open = true;
          }
          if (animate) nextRoot.dataset.catalogEntering = 'true';
          nextRoot.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
          requestAnimationFrame(() => requestAnimationFrame(() => delete nextRoot.dataset.catalogEntering));
        };

        if (animate && document.startViewTransition && !reduceMotion.matches) {
          try {
            const transition = document.startViewTransition(swap);
            transition.ready.catch(() => {});
            transition.updateCallbackDone.catch(() => {});
            await transition.finished.catch(() => {});
          } catch (_) {
            if (!nextRoot.isConnected) swap();
          }
        } else {
          swap();
        }

        if (controller !== currentController) return;
        if (push) window.history.pushState({}, '', url);
        restoreFocus(nextRoot, focus, fallback);
        if (scrollToProducts) nextRoot.scrollIntoView({ block: 'start', behavior: 'auto' });
        announce(nextRoot);
      } catch (error) {
        if (error.name === 'AbortError') return;
        root.removeAttribute('data-catalog-loading');
        root.removeAttribute('aria-busy');
        if (status) status.textContent = 'La mise à jour a échoué. Vous pouvez réessayer.';
      } finally {
        if (controller === currentController) controller = null;
      }
    };

    document.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-catalog-form]');
      if (!form) return;
      event.preventDefault();
      update(requestedUrl(form), { focus: focusSnapshot(document.activeElement) });
    });

    document.addEventListener('change', (event) => {
      const field = event.target.closest('[data-catalog-form] :is(input, select)');
      if (!field || !field.form || !field.form.reportValidity()) return;
      update(requestedUrl(field.form), { focus: focusSnapshot(field) });
    });

    document.addEventListener('click', (event) => {
      const link = event.target.closest(
        '[data-catalog] :is(.catalog-active a, [data-catalog-reset], .catalog-empty a[href*="collection-products"], .collection-pagination__nav a)'
      );
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      const isPagination = Boolean(link.closest('.collection-pagination__nav'));
      update(new URL(link.href, window.location.href), {
        fallback: '#collection-products-title',
        scrollToProducts: isPagination,
        animate: !isPagination,
      });
    });

    window.addEventListener('popstate', () => {
      if (document.querySelector('[data-catalog]')) update(window.location.href, { push: false, animate: false });
    });
    desktop.addEventListener('change', setupCurrentRoot);
    setupRoot(firstRoot);
  }

  /* ─────────────────────────── Démarrage ─────────────────────────── */

  const boot = () => {
    Drawer.init();
    navDrawer();
    stickyBuy();
    recommendations();
    rvRemember();
    rvRender();
    syncSteppers();
    gallery();
    variants();
    expanders();
    catalog();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
